/**
 * Keeps a held lease alive by calling `extend` at roughly a third of the TTL,
 * which tolerates two consecutive failures before the lease actually lapses.
 *
 * Beats are skipped while the tab is hidden so background tabs do not hold a
 * lock the user has walked away from. Returning to a visible tab fires an
 * immediate beat, which doubles as a resync after the timer was throttled.
 */
import { useEffect, useRef } from 'react';

/** Never beat more often than this, however short the server TTL is. */
const MIN_INTERVAL_MS = 30_000;
/** Never go longer than this between beats, however long the TTL is. */
const MAX_INTERVAL_MS = 5 * 60_000;
const DEFAULT_INTERVAL_MS = 60_000;

export function computeHeartbeatIntervalMs(ttlSec: number | null): number {
    if (ttlSec === null || ttlSec <= 0) return DEFAULT_INTERVAL_MS;
    const third = (ttlSec / 3) * 1000;
    return Math.min(MAX_INTERVAL_MS, Math.max(MIN_INTERVAL_MS, third));
}

interface UseLockHeartbeatOptions {
    enabled: boolean;
    ttlSec: number | null;
    onBeat: () => void;
}

export function useLockHeartbeat({ enabled, ttlSec, onBeat }: UseLockHeartbeatOptions): void {
    // Held in a ref so a new callback identity does not restart the interval.
    const onBeatRef = useRef(onBeat);
    useEffect(() => {
        onBeatRef.current = onBeat;
    }, [onBeat]);

    const intervalMs = computeHeartbeatIntervalMs(ttlSec);

    useEffect(() => {
        if (!enabled) return;

        const beatIfVisible = () => {
            if (document.visibilityState === 'visible') onBeatRef.current();
        };

        const intervalId = window.setInterval(beatIfVisible, intervalMs);
        // Resync on return to the tab: the interval may have been throttled and
        // the lease may have been revoked by this user editing elsewhere.
        document.addEventListener('visibilitychange', beatIfVisible);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', beatIfVisible);
        };
    }, [enabled, intervalMs]);
}
